/**
 * <mapa-operacoes> — mapa do Brasil interativo da home (T3, marcadores como
 * blocos do editor desde a T4): N marcadores abrem um card com a
 * corporação atendida.
 *
 * Vanilla, mesmo padrão de assets/hero-cinematic.js (classe HTMLElement
 * simples, sem framework). A revelação do card por :hover em ponteiro fino
 * é 100% CSS (ver {% stylesheet %} da section — `.marcador-grupo:hover
 * .card` e `.marcador[aria-expanded='true'] + .card`); este componente só
 * cobre o que CSS não resolve sozinho: alternar `aria-expanded` no
 * click/Enter/Space, fechar com Esc (devolvendo o foco ao marcador), fechar
 * ao clicar fora do card aberto e coordenar o caso de borda Esc+hover (ver
 * aoTeclado). Sem JS, os marcadores continuam navegáveis e o card
 * simplesmente nunca abre — progressive enhancement.
 *
 * T4: seleção 100% por classe (`.mapa-operacoes__marcador`,
 * `.mapa-operacoes__marcador-grupo`), nada aqui assume 3 marcadores nem os
 * ids fixos bope/rota/pmsc — a section agora itera `section.blocks` e este
 * arquivo não precisou mudar de comportamento. Follow-up opcional (fora de
 * escopo): reagir a `shopify:block:select` pra abrir o card do bloco
 * selecionado no editor.
 */
class MapaOperacoes extends HTMLElement {
  connectedCallback() {
    // Guard de inicialização (achado 9/T8): morph/section-rendering do
    // Shopify pode chamar connectedCallback de novo sem um disconnect real
    // no meio — sem o guard, os listeners de clique por marcador
    // acumulariam a cada passada (disparo múltiplo por clique).
    if (this.inicializado) return;
    this.inicializado = true;

    this.marcadores = Array.from(this.querySelectorAll('.mapa-operacoes__marcador'));
    // Um único handler ligado (bind) reaproveitado em todos os marcadores —
    // evento.currentTarget resolve qual marcador foi clicado, então dá pra
    // remover no disconnectedCallback com a MESMA referência (achado 9),
    // ao contrário de uma arrow function nova por item no forEach.
    this.aoClicarMarcador = this.aoClicarMarcador.bind(this);
    this.marcadores.forEach((marcador) => marcador.addEventListener('click', this.aoClicarMarcador));

    this.aoTeclado = this.aoTeclado.bind(this);
    this.aoApontarFora = this.aoApontarFora.bind(this);
    this.aoSairHover = this.aoSairHover.bind(this);
    document.addEventListener('keydown', this.aoTeclado);
    document.addEventListener('pointerdown', this.aoApontarFora);
  }

  disconnectedCallback() {
    this.inicializado = false;
    this.marcadores.forEach((marcador) => marcador.removeEventListener('click', this.aoClicarMarcador));
    document.removeEventListener('keydown', this.aoTeclado);
    document.removeEventListener('pointerdown', this.aoApontarFora);
  }

  get marcadorAberto() {
    return this.marcadores.find((marcador) => marcador.getAttribute('aria-expanded') === 'true') ?? null;
  }

  aoClicarMarcador(evento) {
    this.alternar(evento.currentTarget);
  }

  alternar(marcadorAlvo) {
    const jaAberto = marcadorAlvo.getAttribute('aria-expanded') === 'true';
    this.fechar();
    if (!jaAberto) marcadorAlvo.setAttribute('aria-expanded', 'true');
  }

  fechar() {
    this.marcadores.forEach((marcador) => marcador.setAttribute('aria-expanded', 'false'));
  }

  aoTeclado(evento) {
    if (evento.key !== 'Escape') return;
    const marcadorAberto = this.marcadorAberto;
    if (!marcadorAberto) return;
    const grupo = marcadorAberto.closest('.mapa-operacoes__marcador-grupo');
    this.fechar();
    marcadorAberto.focus();

    // Achado 10/T8: Esc fecha o estado programático (aria-expanded), mas se
    // o ponteiro segue fisicamente sobre o grupo (comum — quem fecha com
    // teclado enquanto o mouse ainda está sobre o marcador que acabou de
    // clicar), a regra CSS `:hover` continua revelando o card sozinha,
    // dessincronizando visual e aria. `data-esc-fechado` no host suspende
    // essa regra (ver `:not([data-esc-fechado])` no stylesheet da section)
    // até o ponteiro realmente sair do grupo — só então o hover volta a
    // valer, inclusive pra um novo hover no mesmo grupo depois.
    if (grupo?.matches(':hover')) {
      this.setAttribute('data-esc-fechado', '');
      grupo.addEventListener('pointerleave', this.aoSairHover, { once: true });
    }
  }

  aoSairHover() {
    this.removeAttribute('data-esc-fechado');
  }

  aoApontarFora(evento) {
    const marcadorAberto = this.marcadorAberto;
    if (!marcadorAberto) return;
    const grupo = marcadorAberto.closest('.mapa-operacoes__marcador-grupo');
    if (grupo?.contains(evento.target)) return;
    this.fechar();
  }
}

if (!customElements.get('mapa-operacoes')) {
  customElements.define('mapa-operacoes', MapaOperacoes);
}
